import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { SelectInput } from '../../../common/inputs/SelectInput';
import { TextInput } from '../../../common/inputs/TextInput/TextInput';
import { NumberInput } from '../../../common/inputs/NumberInput/NumberInput'; // Aggiunto NumberInput
import ToggleButton from '../../../common/inputs/ToggleButton/ToggleButton';
import MenuButton from '../../../common/MenuButton/MenuButton';
import HierarchicalAccordion from '../../../common/HierarchicalAccordion/HierarchicalAccordion';
import HierarchicalAccordionItem from '../../../common/HierarchicalAccordionItem/HierarchicalAccordionItem';
import Tooltip from '@mui/material/Tooltip'; // Importa Tooltip da MUI
import { HelpCircle } from 'lucide-react'; // Importa l'icona di aiuto
import styles from './ChartsPanel.module.css';
import { chartDefinitions, SpecificChartDefinition, ChartProp, commonComponentDefinitions } from '../hooks/chartDefinitions';
import { useShallow } from 'zustand/react/shallow';
import useGeoData from '../../../store/useGeoData';
import useChartData from '../../../store/useChartData';
import type { ChartConfig, ChartContainerProps, ChartChildComponentConfig } from '../../../store/chartSlice';
import type { GeoDataState } from '../../../store/types';
import type { FeatureCollection, GeoJsonProperties } from 'geojson';
import RechartsColorInput from './custom-inputs/RechartsColorInput'; // Importa il nuovo componente

interface ChartsPanelProps {
  isChartsDisplayVisible: boolean;
  onToggleChartsDisplay: () => void;
}

const ChartsPanel: React.FC<ChartsPanelProps> = ({ isChartsDisplayVisible, onToggleChartsDisplay }) => {
  // Stato per la selezione del tipo di grafico (categoria e specifico)
  const [selectedChartCategoryId, setSelectedChartCategoryId] = useState<string>('cartesian'); // Default a cartesian
  const [selectedSpecificChartId, setSelectedSpecificChartId] = useState<string>('');

  // Dati del layer e nome del grafico
  const [layerFeaturesData, setLayerFeaturesData] = useState<GeoJsonProperties[]>([]);
  const [customChartName, setCustomChartName] = useState<string>('');

  // Stato per il salvataggio/caricamento
  const [saveConfigName, setSaveConfigName] = useState<string>('');
  const [selectedSavedConfigId, setSelectedSavedConfigId] = useState<string>('');


  const { selectedLayerId, dataLayers, mapInstance } = useGeoData(
    useShallow((state: GeoDataState) => ({
      selectedLayerId: state.selectedLayerId,
      dataLayers: state.dataLayers || [],
      mapInstance: state.mapInstance,
    }))
  );

  const {
    activeChartConfig,
    addChartConfig,
    updateChartConfig,
    setActiveChartConfig,
    addChildComponentToActiveChart,
    removeChildComponentFromActiveChart,
    updateChildComponentPropsInActiveChart,
    savedChartConfigurations,
    saveActiveChartConfiguration,
    loadChartConfiguration,
    deleteSavedChartConfiguration,
  } = useChartData(
    useShallow((state) => ({
      activeChartConfig: state.activeChartConfig,
      addChartConfig: state.addChartConfig,
      updateChartConfig: state.updateChartConfig,
      setActiveChartConfig: state.setActiveChartConfig,
      addChildComponentToActiveChart: state.addChildComponentToActiveChart,
      removeChildComponentFromActiveChart: state.removeChildComponentFromActiveChart,
      updateChildComponentPropsInActiveChart: state.updateChildComponentPropsInActiveChart,
      savedChartConfigurations: state.savedChartConfigurations,
      saveActiveChartConfiguration: state.saveActiveChartConfiguration,
      loadChartConfiguration: state.loadChartConfiguration,
      deleteSavedChartConfiguration: state.deleteSavedChartConfiguration,
    }))
  );

  const selectedLayer = useMemo(() => {
    if (!selectedLayerId) return null;
    return dataLayers.find(layer => layer.id === selectedLayerId);
  }, [selectedLayerId, dataLayers]);

  // Estrae i dati dal layer selezionato
  useEffect(() => {
    if (!selectedLayer || !mapInstance) {
      setLayerFeaturesData([]);
      return;
    }
    const sourceSpec = selectedLayer.sourceSpecification as any;
    if (sourceSpec?.type === 'geojson' && typeof sourceSpec.data === 'object' && sourceSpec.data?.type === 'FeatureCollection') {
      setLayerFeaturesData((sourceSpec.data as FeatureCollection).features.map(f => f.properties || {}));
    } else if (selectedLayer.maplibreSourceId && mapInstance && sourceSpec &&
               ((sourceSpec.type === 'geojson' && typeof sourceSpec.data === 'string') ||
                (sourceSpec.type === 'vector' && typeof sourceSpec.url === 'string'))) {
      const queryOptions: { sourceLayer?: string } = {};
      try {
        if (sourceSpec.type === 'vector' && selectedLayer.sourceLayer) {
          queryOptions.sourceLayer = selectedLayer.sourceLayer;
        }
        const featuresFromMap = mapInstance.querySourceFeatures(selectedLayer.maplibreSourceId, queryOptions);
        setLayerFeaturesData(featuresFromMap.map(f => f.properties || {}));
      } catch (error) {
        console.error("Errore durante l'interrogazione delle feature della mappa:", error);
        setLayerFeaturesData([]);
      }
    } else {
      setLayerFeaturesData([]);
    }
  }, [selectedLayer, mapInstance, selectedLayerId]);

  // Opzioni per la select della categoria di grafico (solo cartesiano per ora)
  const chartCategoryOptions = useMemo(() => {
    const cartesianCategory = chartDefinitions.find(cat => cat.id === 'cartesian');
    return cartesianCategory ? [{ value: cartesianCategory.id, label: cartesianCategory.label }] : [];
  }, []);

  // Opzioni per la select del grafico specifico (basato sulla categoria cartesiana)
  const specificChartOptions = useMemo(() => {
    const cartesianCategory = chartDefinitions.find(cat => cat.id === selectedChartCategoryId);
    if (!cartesianCategory) return [];
    return cartesianCategory.charts.map((chart: SpecificChartDefinition) => ({
      value: chart.id, // Usa l'id della definizione (es. 'line', 'bar')
      label: chart.label,
    }));
  }, [selectedChartCategoryId]);

  // Definizione del grafico specifico selezionato
  const selectedSpecificChartDefinition = useMemo(() => {
    if (!selectedChartCategoryId || !selectedSpecificChartId) return null;
    const category = chartDefinitions.find(cat => cat.id === selectedChartCategoryId);
    return category?.charts.find(chart => chart.id === selectedSpecificChartId) || null;
  }, [selectedChartCategoryId, selectedSpecificChartId]);


  // Imposta il default per selectedSpecificChartId quando le opzioni cambiano
  useEffect(() => {
    if (specificChartOptions.length > 0) {
      const currentSelectionIsValid = specificChartOptions.some(opt => opt.value === selectedSpecificChartId);
      if (!selectedSpecificChartId || !currentSelectionIsValid) {
        setSelectedSpecificChartId(specificChartOptions[0].value);
      }
    } else {
      setSelectedSpecificChartId('');
    }
  }, [specificChartOptions, selectedSpecificChartId]);


  const handleAddAndActivateChart = useCallback(() => {
    if (!selectedSpecificChartDefinition || !selectedLayerId || !selectedLayer) {
      console.warn("Selezionare un tipo di grafico e un layer prima di aggiungere.");
      return;
    }

    const defaultContainerProps: ChartContainerProps = {};
    selectedSpecificChartDefinition.props.forEach(prop => {
      if (prop.category === 'Layout' || prop.category === 'Data' || prop.name === 'syncId') { // Includi solo props del contenitore
        if (prop.defaultValue !== undefined) {
          defaultContainerProps[prop.name] = prop.defaultValue;
        }
      }
    });
    // Assicura che 'data' sia inclusa, anche se non ha un defaultValue in chartDefinitions
    defaultContainerProps.data = layerFeaturesData;


    const newChartId = Date.now().toString();
    const newChartConfig: ChartConfig = {
      id: newChartId,
      chartComponentType: selectedSpecificChartDefinition.componentName,
      chartContainerProps: {
        ...defaultContainerProps,
        data: layerFeaturesData, // Assicura che i dati siano passati
      },
      layerId: selectedLayerId,
      name: customChartName.trim() !== '' ? customChartName.trim() : `${selectedSpecificChartDefinition.label} - ${selectedLayer?.name || 'Layer Sconosciuto'}`,
      childComponents: [], // Inizializza childComponents come array vuoto
    };

    addChartConfig(newChartConfig);
    setActiveChartConfig(newChartId); // Imposta il nuovo grafico come attivo
    setCustomChartName('');
  }, [selectedSpecificChartDefinition, selectedLayerId, layerFeaturesData, customChartName, addChartConfig, setActiveChartConfig, selectedLayer]);

  const handleContainerPropChange = useCallback((propName: string, value: any, isNumeric: boolean = false) => {
    if (!activeChartConfig) return;

    let processedValue = value;
    if (isNumeric) {
      processedValue = value === '' ? undefined : value;
    }

    const updates: Partial<ChartContainerProps> = { [propName]: processedValue };
    updateChartConfig(activeChartConfig.id, { chartContainerProps: updates });
  }, [activeChartConfig, updateChartConfig]);

  const handleMarginChange = useCallback((subPropName: 'top' | 'right' | 'bottom' | 'left', value: number | string) => {
      if (!activeChartConfig) return;
  
      // NumberInput restituisce un numero o una stringa vuota.
      // Se è una stringa vuota, vogliamo che il valore sia undefined.
      // Se è un numero, lo usiamo direttamente.
      const processedValue = typeof value === 'string' && value === '' ? undefined : value as number;
  
      const currentMargins = activeChartConfig.chartContainerProps?.margin || {};
      const updatedMargins = {
        ...currentMargins,
        [subPropName]: processedValue,
      };
      updateChartConfig(activeChartConfig.id, { chartContainerProps: { margin: updatedMargins } });
    }, [activeChartConfig, updateChartConfig]);


  const renderInputControl = (prop: ChartProp, horizontalInternalLayout: boolean = false) => {
    if (!activeChartConfig || !selectedSpecificChartDefinition) return null;
    // Filtra per mostrare solo le props del contenitore
    // Questa logica di filtro esterna al renderInputControl se width/height sono gestite separatamente
    if (prop.category !== 'Layout' && prop.category !== 'Data' && prop.name !== 'syncId' && prop.name !== 'layout') {
        // 'layout' è una prop del contenitore per BarChart/ComposedChart
        if (selectedSpecificChartDefinition.componentName === 'BarChart' && prop.name === 'layout') {
            // Non fare nulla, la prop 'layout' è valida
        } else if (selectedSpecificChartDefinition.componentName === 'ComposedChart' && prop.name === 'layout') {
            // Non fare nulla, la prop 'layout' è valida
        }
        else if (prop.name !== 'data') { // 'data' è una prop del contenitore
             return null;
        }
    }


    const currentValue = activeChartConfig.chartContainerProps?.[prop.name] ?? prop.defaultValue;

    switch (prop.inputType) {
      case 'number':
        if (horizontalInternalLayout) { // Usato per width/height in coppia o input di margine che usano chartNumericControlWrapperSizing
          return (
            <div className={styles.chartNumericControlWrapperSizing}>
              <div className={styles.labelWrapper}>
                <label htmlFor={`prop-${prop.name}`} className={styles.label}>
                  {prop.label}
                </label>
                {prop.description && (
                  <Tooltip title={prop.description} arrow>
                    <span className={styles.helpIconWrapper}>
                      <HelpCircle size={14} className={styles.helpIcon} />
                    </span>
                  </Tooltip>
                )}
              </div>
              <NumberInput
                id={`prop-${prop.name}`}
                value={currentValue ?? ''}
                onChange={(val) => handleContainerPropChange(prop.name, val, true)}
                placeholder={prop.placeholder || String(prop.defaultValue)}
                min={prop.name === 'width' || prop.name === 'height' ? 1 : undefined}
              />
            </div>
          );
        } else { // Per input numerici singoli a larghezza piena
          return (
            <div className={styles.inputControlWrapper}>
              <div className={styles.inputFlexItem}>
                <div className={styles.labelWrapper}>
                  <label htmlFor={`prop-${prop.name}`} className={styles.label}>
                    {prop.label}
                  </label>
                  {prop.description && (
                    <Tooltip title={prop.description} arrow>
                      <span className={styles.helpIconWrapper}>
                        <HelpCircle size={14} className={styles.helpIcon} />
                      </span>
                    </Tooltip>
                  )}
                </div>
                <NumberInput
                  id={`prop-${prop.name}`}
                  value={currentValue ?? ''}
                  onChange={(val) => handleContainerPropChange(prop.name, val, true)}
                  placeholder={prop.placeholder || String(prop.defaultValue)}
                />
              </div>
            </div>
          );
        }
      case 'select':
        return (
          <div className={styles.inputControlWrapper}>
            <div className={styles.inputFlexItem}>
              <div className={styles.labelWrapper}>
                <label htmlFor={`prop-${prop.name}`} className={styles.label}>
                  {prop.label}
                </label>
                {prop.description && (
                  <Tooltip title={prop.description} arrow>
                    <span className={styles.helpIconWrapper}>
                      <HelpCircle size={14} className={styles.helpIcon} />
                    </span>
                  </Tooltip>
                )}
              </div>
              <SelectInput
                id={`prop-${prop.name}`}
                options={prop.options!.map(opt => typeof opt === 'string' ? { value: opt, label: opt } : opt)}
                value={currentValue ?? ''}
                onChange={(e) => handleContainerPropChange(prop.name, e.target.value)}
              />
            </div>
          </div>
        );
      case 'string':
        return (
          <div className={styles.inputControlWrapper}>
            <div className={styles.inputFlexItem}>
              <div className={styles.labelWrapper}>
                <label htmlFor={`prop-${prop.name}`} className={styles.label}>
                  {prop.label}
                </label>
                {prop.description && (
                  <Tooltip title={prop.description} arrow>
                    <span className={styles.helpIconWrapper}>
                      <HelpCircle size={14} className={styles.helpIcon} />
                    </span>
                  </Tooltip>
                )}
              </div>
              <TextInput
                id={`prop-${prop.name}`}
                type="text"
                value={currentValue ?? ''}
                onChange={(e) => handleContainerPropChange(prop.name, e.target.value)}
                placeholder={prop.placeholder || String(prop.defaultValue)}
              />
            </div>
          </div>
        );
      case 'object':
        if (prop.name === 'margin') {
          const margin = currentValue as ChartContainerProps['margin'] || {};
          return (
            // Manteniamo inputControlWrapper per coerenza con gli altri controlli.
            // Rimuoviamo marginGroupContainer che potrebbe causare l'indentazione.
            <div className={styles.inputControlWrapper}> {/* CORRETTO: Ripristinato a inputControlWrapper e rimosso commento JSX errato */}
              {/* Etichetta principale "Margini", ora allo stesso livello delle altre etichette */}
              <div className={styles.labelWrapper}>
                <label htmlFor={`prop-${prop.name}-group`} className={styles.label}> {/* Usa styles.label per coerenza */}
                  {prop.label}
                </label>
                {prop.description && (
                  <Tooltip title={prop.description} arrow>
                    <span className={styles.helpIconWrapper}>
                      <HelpCircle size={14} className={styles.helpIcon} />
                    </span>
                  </Tooltip>
                )}
              </div>

              {/* Contenitore per i 4 input dei margini, che userà CSS Grid */}
              {/* Riutilizziamo la classe styles.marginInputsWrapper esistente che già usa CSS Grid. */}
              <div className={styles.marginInputsWrapper}> {/* Usa la classe esistente per CSS Grid */}
                {['top', 'right', 'bottom', 'left'].map((dir) => (
                  // styles.marginInputItem è sostituito da styles.chartNumericControlWrapperSizing
                  <div key={dir} className={styles.chartNumericControlWrapperSizing}>
                    <label htmlFor={`prop-margin-${dir}`} className={styles.labelSmall}>
                      {dir.charAt(0).toUpperCase() + dir.slice(1)}
                    </label>
                    <NumberInput
                      id={`prop-margin-${dir}`}
                      value={margin[dir] === undefined ? '' : margin[dir]}
                      onChange={(val) => handleMarginChange(dir as 'top' | 'right' | 'bottom' | 'left', val)}
                      placeholder={String(prop.defaultValue?.[dir] ?? '0')}
                      min={0}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        }
        return <p>Configurazione oggetto non supportata per: {prop.label}</p>;
      case 'boolean':
         // Non ci sono props booleane per il contenitore in questa fase
        return null;
      case 'dataKey': // Non per il contenitore
      case 'array': // 'data' è gestita a livello superiore
      default:
        return null; // Non renderizzare input per tipi non gestiti o non pertinenti al contenitore
    }
  };
  
  const isAddChartDisabled = useMemo(() => {
    return !selectedSpecificChartDefinition || !selectedLayerId || layerFeaturesData.length === 0;
  }, [selectedSpecificChartDefinition, selectedLayerId, layerFeaturesData]);

  const handleSpecificAddComponent = useCallback((componentType: string) => {
    if (!activeChartConfig) {
      console.warn("Nessun grafico attivo per aggiungere il componente:", componentType);
      return;
    }

    const definition = commonComponentDefinitions[componentType];
    const defaultProps: { [key: string]: any } = {};

    if (definition && Array.isArray(definition)) {
      definition.forEach(prop => {
        if (prop.defaultValue !== undefined) {
          defaultProps[prop.name] = prop.defaultValue;
        }
      });
    } else {
      console.warn(`Definizione delle props per ${componentType} non trovata in commonComponentDefinitions o non è un array. Il componente verrà aggiunto senza props di default.`);
    }
    addChildComponentToActiveChart(componentType, defaultProps);
  }, [activeChartConfig, addChildComponentToActiveChart]);

  const handleUpdateChildProps = useCallback((childId: string, propName: string, value: any) => {
    if (!activeChartConfig) return;
    updateChildComponentPropsInActiveChart(childId, { [propName]: value });
  }, [activeChartConfig, updateChildComponentPropsInActiveChart]);


  const renderChildComponentInputControl = (prop: ChartProp, childComponent: ChartChildComponentConfig) => {
    if (!activeChartConfig) return null;

    const currentValue = childComponent.props?.[prop.name] ?? prop.defaultValue;
    const inputId = `child-prop-${childComponent.id}-${prop.name}`;

    const handleChange = (value: any, isNumeric: boolean = false) => {
      let processedValue = value;
      if (isNumeric) {
        processedValue = value === '' ? undefined : value;
      }
      handleUpdateChildProps(childComponent.id, prop.name, processedValue);
    };

    // Helper per non ripetere il blocco label + description + tooltip
    const renderLabelWithTooltip = (propForLabel: ChartProp, inputIdForLabel: string) => (
      <div className={styles.labelWrapper}>
        <label htmlFor={inputIdForLabel} className={styles.label}>
          {propForLabel.label}
        </label>
        {propForLabel.description && (
          <Tooltip title={propForLabel.description} arrow>
            <span className={styles.helpIconWrapper}>
              <HelpCircle size={14} className={styles.helpIcon} />
            </span>
          </Tooltip>
        )}
      </div>
    );

    switch (prop.inputType) {
      case 'number':
        return (
          <div className={styles.inputControlWrapper}> {/* Usa la classe CSS corretta per il wrapper */}
            <div className={styles.inputFlexItem}> {/* Wrapper per etichetta e input */}
              {renderLabelWithTooltip(prop, inputId)}
              <NumberInput
                id={inputId}
                value={currentValue ?? ''}
                onChange={(val) => handleChange(val, true)}
                placeholder={prop.placeholder || String(prop.defaultValue)}
                className={styles.compactNumberInputForChild}
              />
            </div>
          </div>
        );
      case 'string':
        if (prop.options && Array.isArray(prop.options)) {
          return (
          <div className={styles.inputControlWrapper}>
            <div className={styles.inputFlexItem}>
              {renderLabelWithTooltip(prop, inputId)}
              <SelectInput
                id={inputId}
                options={prop.options.map(opt => typeof opt === 'string' ? { value: opt, label: opt } : opt)}
                value={currentValue ?? ''}
                onChange={(e) => handleChange(e.target.value)}
              />
            </div>
          </div>
          );
        }
        return (
          <div className={styles.inputControlWrapper}>
            <div className={styles.inputFlexItem}>
              {renderLabelWithTooltip(prop, inputId)}
              <TextInput
                id={inputId}
                type="text"
                value={currentValue === undefined ? '' : String(currentValue)}
                onChange={(e) => handleChange(e.target.value)}
                placeholder={prop.placeholder || String(prop.defaultValue)}
                className={styles.compactTextInputForChild}
              />
            </div>
          </div>
        );
      case 'text':
        return (
          <div className={styles.inputControlWrapper}>
            <div className={styles.inputFlexItem}>
              {renderLabelWithTooltip(prop, inputId)}
              <TextInput
                id={inputId}
                type="text"
                value={currentValue === undefined ? '' : String(currentValue)}
                onChange={(e) => handleChange(e.target.value)}
                placeholder={prop.placeholder || String(prop.defaultValue ?? '')}
                className={styles.compactTextInputForChild}
              />
            </div>
          </div>
        );
      case 'color':
        return (
          <div className={styles.inputControlWrapper}>
            <div className={styles.inputFlexItem}>
              {renderLabelWithTooltip(prop, inputId)}
              <RechartsColorInput
                id={inputId}
                value={currentValue ?? (prop.defaultValue as string) ?? '#000000'}
                onColorChange={(colorValue: string) => handleChange(colorValue)}
                className={styles.compactColorInputForChild}
              />
            </div>
          </div>
        );
      case 'boolean':
        // booleanToggleContainer già include inputControlWrapper e gestisce il layout
        return (
          // Applica inputControlWrapper per coerenza con gli altri controlli child
          <div className={styles.inputControlWrapper}>
            <div className={styles.inputFlexItem}>
              {renderLabelWithTooltip(prop, inputId)}
              <ToggleButton
                className={styles.childControlToggleButton}
                id={inputId}
                isOn={currentValue ?? false}
                onToggle={() => handleChange(!currentValue)}
                label="" // Il label è gestito da renderLabelWithTooltip
              />
            </div>
          </div>
        );
      case 'dataKey':
         const dataKeysOptions = layerFeaturesData.length > 0 && layerFeaturesData[0]
            ? Object.keys(layerFeaturesData[0]).map(key => ({ value: key, label: key }))
            : [];
        return (
            <div className={styles.inputControlWrapper}>
              <div className={styles.inputFlexItem}>
                {renderLabelWithTooltip(prop, inputId)}
                <SelectInput
                    id={inputId}
                    options={dataKeysOptions}
                    value={currentValue ?? ''}
                    onChange={(e) => handleChange(e.target.value)}
                    disabled={dataKeysOptions.length === 0}
                    className={styles.compactSelectInputForChild}
                />
              </div>
            </div>
        );
      case 'select':
        return (
          <div className={styles.inputControlWrapper}>
            <div className={styles.inputFlexItem}>
              {renderLabelWithTooltip(prop, inputId)}
              <SelectInput
                id={inputId}
                options={prop.options?.map(opt => typeof opt === 'string' ? { value: opt, label: opt } : opt) ?? []}
                value={currentValue ?? ''}
                onChange={(e) => handleChange(e.target.value)}
                className={styles.compactSelectInputForChild}
              />
            </div>
          </div>
        );
      case 'object':
        return (
          // Aggiungiamo 'objectWrapper' se vogliamo stili specifici come per textareaWrapper
          <div className={`${styles.inputControlWrapper} ${styles.textareaWrapper} ${styles.objectLayoutWrapper}`}>
            <div className={styles.inputFlexItem}>
              {renderLabelWithTooltip(prop, inputId)}
              <textarea
                id={inputId}
                value={currentValue !== undefined ? JSON.stringify(currentValue, null, 2) : ''}
                onChange={(e) => {
                  try {
                    const valToParse = e.target.value.trim();
                    if (valToParse === '') {
                      handleChange(undefined);
                    } else {
                      const parsedJson = JSON.parse(valToParse);
                      handleChange(parsedJson);
                    }
                  } catch (error) {
                    console.warn(`Invalid JSON for prop ${prop.name}:`, e.target.value, error);
                  }
                }}
                placeholder={prop.placeholder || (prop.defaultValue !== undefined ? JSON.stringify(prop.defaultValue, null, 2) : 'Enter JSON object')}
                className={styles.jsonTextarea}
                rows={5}
              />
            </div>
          </div>
        );
      case 'textarea':
        return (
          <div className={`${styles.inputControlWrapper} ${styles.textareaWrapper}`}>
            <div className={styles.inputFlexItem}>
              {renderLabelWithTooltip(prop, inputId)}
              <textarea
                id={inputId}
                value={currentValue === undefined ? '' : String(currentValue)}
                onChange={(e) => handleChange(e.target.value)}
                placeholder={prop.placeholder || String(prop.defaultValue ?? '')}
                className={styles.genericTextarea}
                rows={3}
              />
            </div>
          </div>
        );
      case 'info':
      case 'unsupported':
        return (
          <div className={styles.inputControlWrapper}>
            <div className={styles.inputFlexItem}> {/* Anche per info/unsupported per coerenza di layout */}
              {renderLabelWithTooltip(prop, inputId)}
              <span className={styles.infoText}>
                {prop.inputType === 'info' ? '(Info)' : '(Non Supportato)'}
              </span>
            </div>
          </div>
        );
      default:
        return (
          <div className={styles.inputControlWrapper}>
            <div className={styles.inputFlexItem}>
              {renderLabelWithTooltip(prop, inputId)}
              <p className={styles.unsupportedText}>Tipo di input non supportato: {prop.inputType} per {prop.label}</p>
            </div>
          </div>
        );
    }
  };

  const handleSaveConfiguration = () => {
    if (!activeChartConfig) {
      alert("Nessun grafico attivo da salvare.");
      return;
    }
    if (!saveConfigName.trim()) {
      alert("Inserisci un nome per la configurazione.");
      return;
    }
    saveActiveChartConfiguration(saveConfigName.trim());
    setSaveConfigName(''); // Resetta il nome dopo il salvataggio
    alert(`Configurazione "${saveConfigName.trim()}" salvata!`);
  };

  const handleLoadConfiguration = () => {
    if (!selectedSavedConfigId) {
      alert("Seleziona una configurazione da caricare.");
      return;
    }
    loadChartConfiguration(selectedSavedConfigId);
    // Potrebbe essere necessario aggiornare selectedSpecificChartId e selectedChartCategoryId
    // in base alla configurazione caricata, se si vuole che i selettori riflettano il tipo caricato.
    // Per ora, il caricamento imposta activeChartConfig, e l'UI dovrebbe rifletterlo.
    const loadedConfig = savedChartConfigurations.find(sc => sc.savedId === selectedSavedConfigId);
    if (loadedConfig) {
        // Trova la definizione del grafico caricato per aggiornare i selettori
        const category = chartDefinitions.find(cat =>
            cat.charts.some(chartDef => chartDef.componentName === loadedConfig.config.chartComponentType)
        );
        if (category) {
            setSelectedChartCategoryId(category.id);
            const specificChart = category.charts.find(chartDef => chartDef.componentName === loadedConfig.config.chartComponentType);
            if (specificChart) {
                setSelectedSpecificChartId(specificChart.id);
            }
        }
        setCustomChartName(loadedConfig.config.name); // Aggiorna il titolo del grafico
    }
    alert(`Configurazione caricata!`);
  };

  const handleDeleteConfiguration = () => {
    if (!selectedSavedConfigId) {
      alert("Seleziona una configurazione da eliminare.");
      return;
    }
     
    if (confirm(`Sei sicuro di voler eliminare la configurazione selezionata?`)) {
      deleteSavedChartConfiguration(selectedSavedConfigId);
      setSelectedSavedConfigId(''); // Deseleziona dopo l'eliminazione
      alert("Configurazione eliminata.");
    }
  };

  const savedConfigsOptions = useMemo(() => {
    const options = savedChartConfigurations.map(sc => ({ value: sc.savedId, label: `${sc.name} (ID: ${sc.savedId.substring(0,6)})` }));
    // Aggiungi un'opzione placeholder se non ci sono configurazioni o nessuna è selezionata
    return [
      { value: '', label: 'Seleziona una configurazione...', disabled: true },
      ...options
    ];
  }, [savedChartConfigurations]);

  // Effetto per assicurarsi che selectedSavedConfigId sia valido o vuoto
  useEffect(() => {
    if (selectedSavedConfigId && !savedChartConfigurations.find(sc => sc.savedId === selectedSavedConfigId)) {
      setSelectedSavedConfigId(''); // Resetta se l'ID selezionato non è più valido (es. dopo eliminazione)
    }
    if (!selectedSavedConfigId && savedChartConfigurations.length > 0) {
        // Non impostare un default qui per permettere al placeholder di funzionare
    }
  }, [savedChartConfigurations, selectedSavedConfigId]);


  return (
    <div className={styles.chartsPanelContainer}>
      <HierarchicalAccordion defaultOpenId="create-select-chart"> {/* defaultOpenId può essere il primo item o null */}
        {/* Sezione Creazione/Selezione Grafico */}
        <HierarchicalAccordionItem id="create-select-chart" title="Choose Chart" level="secondary">
          <div className={styles.accordionContentPadding}> {/* Aggiunto padding interno */}
            <div className={styles.formRow}>
              <label htmlFor="chartCategorySelect" className={styles.label}>
                Category
          </label>
          <SelectInput
            id="chartCategorySelect"
            options={chartCategoryOptions}
            value={selectedChartCategoryId}
            onChange={(e) => {
              setSelectedChartCategoryId(e.target.value);
              setSelectedSpecificChartId('');
            }}
            disabled={chartCategoryOptions.length <= 1}
          />
        </div>

        {selectedChartCategoryId && specificChartOptions.length > 0 && (
          <div className={styles.formRow}>
            <label htmlFor="specificChartSelect" className={styles.label}>
              Type
            </label>
            <SelectInput
              id="specificChartSelect"
              options={specificChartOptions}
              value={selectedSpecificChartId}
              onChange={(e) => setSelectedSpecificChartId(e.target.value)}
              disabled={!specificChartOptions.length}
            />
          </div>
        )}
        
        <div className={styles.formRow}>
          <label htmlFor="customChartNameInput" className={styles.label}>
            Title
          </label>
          <TextInput
            id="customChartNameInput"
            value={customChartName}
            onChange={(e) => setCustomChartName(e.target.value)}
            placeholder="Inserisci titolo grafico"
          />
        </div>

        <div className={styles.buttonContainer}>
          <MenuButton
            onClick={handleAddAndActivateChart}
            disabled={isAddChartDisabled}
            title={isAddChartDisabled ? "Seleziona tipo di grafico e layer" : "Add & Configure Chart"}
          >
            Add & Configure Chart
              </MenuButton>
            </div>
          </div>
        </HierarchicalAccordionItem>

        {/* Sezione Configurazione Grafico Attivo */}
        {activeChartConfig && selectedSpecificChartDefinition && (
          <HierarchicalAccordionItem
            id="configure-active-chart"
            title={`Config: ${activeChartConfig.name} (${selectedSpecificChartDefinition.label})`}
            level="secondary"
          >
            <div className={styles.accordionContentPadding}> {/* Aggiunto padding interno */}
              {(() => {
                const allProps = selectedSpecificChartDefinition.props
                  .filter(prop => prop.category === 'Layout' || prop.name === 'syncId' || prop.name === 'data' || (selectedSpecificChartDefinition.componentName === 'BarChart' && prop.name === 'layout') || (selectedSpecificChartDefinition.componentName === 'ComposedChart' && prop.name === 'layout'));

                const widthPropDef = allProps.find(p => p.name === 'width');
                const heightPropDef = allProps.find(p => p.name === 'height');
                
                const otherProps = allProps.filter(p => p.name !== 'width' && p.name !== 'height');

                return (
                  <>
                    {widthPropDef && heightPropDef && (
                      <div className={styles.dimensionInputsRow}>
                        {/* RenderInputControl per width e height ora restituisce un div che si adatta al flex container */}
                        {renderInputControl(widthPropDef, true)}
                        {renderInputControl(heightPropDef, true)}
                      </div>
                    )}
                    {/* Renderizza width o height se solo uno dei due è definito e non insieme */}
                    {widthPropDef && !heightPropDef && (
                        <div key={widthPropDef.name} className={styles.propControl}>
                            {renderInputControl(widthPropDef)}
                        </div>
                    )}
                    {!widthPropDef && heightPropDef && (
                        <div key={heightPropDef.name} className={styles.propControl}>
                            {renderInputControl(heightPropDef)}
                        </div>
                    )}

                    {otherProps.map(prop => (
                      <div key={prop.name} className={styles.propControl}>
                        {renderInputControl(prop)}
                      </div>
                    ))}
                  </>
                );
              })()}

              <div className={styles.addChildComponentSection}>
                <div className={styles.specificAddComponentButtonsContainer}>
              {(selectedSpecificChartDefinition?.componentName === 'LineChart' || selectedSpecificChartDefinition?.componentName === 'ComposedChart') && (
                <MenuButton onClick={() => handleSpecificAddComponent('Line')} className={styles.addComponentButton}>
                  Line
                </MenuButton>
              )}
              {(selectedSpecificChartDefinition?.componentName === 'BarChart' || selectedSpecificChartDefinition?.componentName === 'ComposedChart') && (
                <MenuButton onClick={() => handleSpecificAddComponent('Bar')} className={styles.addComponentButton}>
                  Bar
                </MenuButton>
              )}
              {(selectedSpecificChartDefinition?.componentName === 'AreaChart' || selectedSpecificChartDefinition?.componentName === 'ComposedChart') && (
                <MenuButton onClick={() => handleSpecificAddComponent('Area')} className={styles.addComponentButton}>
                  Area
                </MenuButton>
              )}
              {(selectedSpecificChartDefinition?.componentName === 'ScatterChart' || selectedSpecificChartDefinition?.componentName === 'ComposedChart') && (
                <MenuButton onClick={() => handleSpecificAddComponent('Scatter')} className={styles.addComponentButton}>
                  Scatter
                </MenuButton>
              )}
              {selectedChartCategoryId === 'cartesian' && (
                <>
                  <MenuButton onClick={() => handleSpecificAddComponent('YAxis')} className={styles.addComponentButton}>
                    YAxis
                  </MenuButton>
                  <MenuButton onClick={() => handleSpecificAddComponent('ReferenceLine')} className={styles.addComponentButton}>
                    ReferenceLine
                  </MenuButton>
                  <MenuButton onClick={() => handleSpecificAddComponent('ReferenceArea')} className={styles.addComponentButton}>
                    ReferenceArea
                  </MenuButton>
                  <MenuButton onClick={() => handleSpecificAddComponent('XAxis')} className={styles.addComponentButton}>XAxis</MenuButton>
                  <MenuButton onClick={() => handleSpecificAddComponent('CartesianGrid')} className={styles.addComponentButton}>Grid</MenuButton>
                  <MenuButton onClick={() => handleSpecificAddComponent('Tooltip')} className={styles.addComponentButton}>Tooltip</MenuButton>
                  <MenuButton onClick={() => handleSpecificAddComponent('Legend')} className={styles.addComponentButton}>Legend</MenuButton>
                </>
              )}
            </div>
          </div>

          {activeChartConfig.childComponents && activeChartConfig.childComponents.map(child => {
            const definition = commonComponentDefinitions[child.type];
            let title = `${child.type}`;
            if (child.props?.name) {
              title += `: ${child.props.name}`;
            } else if (child.props?.dataKey) {
              title += `: ${child.props.dataKey}`;
            } else {
              title += ` (ID: ${child.id.substring(0,6)})`;
            }

            return (
              // Avvolgiamo ogni HierarchicalAccordionItem in un suo HierarchicalAccordion
              // per permettere l'apertura/chiusura individuale e lo stato chiuso di default.
              <HierarchicalAccordion key={`accordion-for-${child.id}`} defaultOpenId={null}>
                <HierarchicalAccordionItem
                  id={`child-item-${child.id}`} // ID univoco per l'item all'interno del suo Accordion
                  title={title}
                  level="tertiary" // o un altro livello appropriato
                >
                  {/* Il div className={styles.childComponentConfig} è stato rimosso */}
                  <div className={styles.childHeader}>
                    {/* Il titolo è ora nell'HierarchicalAccordionItem */}
                    <span className={styles.flexGrowSpacer}></span> {/* Per spaziare il bottone a destra */}
                    <MenuButton
                      onClick={() => removeChildComponentFromActiveChart(child.id)}
                      className={styles.removeChildButton}
                      title={`Rimuovi ${child.type}`}
                    >
                      Rimuovi
                    </MenuButton>
                  </div>
                  {Array.isArray(definition) && definition.map(propDef => (
                    // Il div con styles.propControl è stato rimosso.
                    // renderChildComponentInputControl restituisce già l'elemento corretto con styles.inputControlWrapper
                    // Aggiungiamo la key all'elemento restituito da renderChildComponentInputControl.
                    React.cloneElement(renderChildComponentInputControl(propDef, child) as React.ReactElement, { key: `${child.id}-prop-${propDef.name}` })
                  ))}
                </HierarchicalAccordionItem>
              </HierarchicalAccordion>
            );
          })}
            </div>
          </HierarchicalAccordionItem>
        )}

        {/* Sezione Gestione Configurazioni */}
        {(activeChartConfig || savedChartConfigurations.length > 0) && (
          <HierarchicalAccordionItem id="manage-configurations" title="Save&Export Config" level="secondary">
            <div className={styles.accordionContentPadding}> {/* Aggiunto padding interno */}
              {/* Sotto-sezione Salvataggio Configurazione */}
              {activeChartConfig && (
                <div className={`${styles.subSection} ${styles.saveLoadSection}`}>
                  <h5>Salva Configurazione Grafico Attivo</h5>
                  <div className={styles.formRow}>
                    <label htmlFor="saveConfigNameInput" className={styles.label}>
                      Nome Configurazione
            </label>
            <TextInput
              id="saveConfigNameInput"
              value={saveConfigName}
              onChange={(e) => setSaveConfigName(e.target.value)}
              placeholder="Es. Grafico Vendite Q1"
              className={styles.configNameInput}
            />
          </div>
          <MenuButton
            onClick={handleSaveConfiguration}
            disabled={!saveConfigName.trim() || !activeChartConfig}
          >
                    Salva Configurazione
                  </MenuButton>
                </div>
              )}

              {/* Sotto-sezione Caricamento Configurazione */}
              {savedChartConfigurations.length > 0 && (
                <div className={`${styles.subSection} ${styles.saveLoadSection}`}>
                  <h5>Carica Configurazione Grafico</h5>
                  <div className={styles.formRow}>
                    <label htmlFor="loadConfigSelect" className={styles.label}>
                      Configurazioni Salvate
            </label>
            <SelectInput
              id="loadConfigSelect"
              options={savedConfigsOptions}
              value={selectedSavedConfigId || ''} // Assicura che il valore sia vuoto se non selezionato per il placeholder
              onChange={(e) => setSelectedSavedConfigId(e.target.value)}
              className={styles.configSelectInput}
            />
          </div>
          <div className={styles.configActionsRow}>
            <MenuButton
              onClick={handleLoadConfiguration}
              disabled={!selectedSavedConfigId}
              className={styles.flexButton}
            >
              Carica Selezionata
            </MenuButton>
            <MenuButton
              onClick={handleDeleteConfiguration}
              disabled={!selectedSavedConfigId}
              className={`${styles.flexButton} ${styles.deleteConfigButton}`}
              title="Elimina configurazione selezionata"
            >
              Elimina Selezionata
                    </MenuButton>
                  </div>
                </div>
              )}
            </div>
          </HierarchicalAccordionItem>
        )}
      </HierarchicalAccordion>

      <div className={`${styles.toggleDisplaySection} ${styles.formRow}`}>
        <ToggleButton
          id="toggle-charts-display"
          isOn={isChartsDisplayVisible}
          onToggle={onToggleChartsDisplay}
          label={isChartsDisplayVisible ? "Nascondi Grafici" : "Mostra Grafici"}
        />
      </div>
    </div>
  );
};

export default ChartsPanel;